import React, { useLayoutEffect, useRef } from "react";

/**
 * AutoTextarea — a textarea that is as tall as its text.
 *
 * Drop-in for <textarea>: same props, same style. Three rules:
 *   • Empty: the height the caller asked for (`rows`, default 2) — a field
 *     with nothing in it still looks like a field.
 *   • With text: exactly the text's height plus half a line of air at the
 *     bottom, so the eye can see it is the end and there is room to type —
 *     one short line gets a one-line box, not the empty-field height.
 *   • Long: capped at `maxRows` (default 18, roughly a phone screen) and
 *     scrolls inside from there.
 * Height is recomputed on every value change and on resize; the user can
 * still drag the handle (resize: vertical) and the next keystroke respects
 * the content again. Styles passed in win over the defaults.
 */
export default function AutoTextarea({ value, rows = 2, maxRows = 18, style, onChange, ...rest }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const cs = window.getComputedStyle(el);
    const line = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.4 || 20;
    const pad = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const border = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    const minH = rows * line + pad + border;
    const maxH = maxRows * line + pad + border;
    if (!value) {
      el.style.height = `${minH}px`;
      el.style.overflowY = "hidden";
      return;
    }
    el.style.height = "0px";                       // let scrollHeight measure the text
    const wanted = el.scrollHeight + border + line * 0.5;
    const oneLine = line + pad + border;
    const h = Math.min(Math.max(wanted, oneLine), maxH);
    el.style.height = `${h}px`;
    el.style.overflowY = wanted > maxH ? "auto" : "hidden";
  }, [value, rows, maxRows]);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      rows={rows}
      style={{ boxSizing: "border-box", resize: "vertical", ...(style || {}) }}
      {...rest}
    />
  );
}
